import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useStateSelector } from '../../../store/selectors';
import { addNotificationFlagToDelete } from '../../../slices/notification-flags/notification-flags.slice';
import {
    fetchLegalTermsSummary,
    updateLegalTermRowComment,
    updateLegalTermRowInterpretation,
} from '../../../slices/marketplace/legal-terms-summary/legal-terms-summary-api';
import {
    LegalTermRow,
    LegalTermSection,
    LegalTermsSummary,
} from '../../../models/LegalTermsSummary/legal-terms-summary.model';
import { LegalTermsSummarySubTab, LegalTermsSummarySubTabs } from './legal-terms-summary-sub-tab.enum';
import { renderLoiAnswerReadonly } from '../../../slices/marketplace/loi-form/render-loi-answer-readonly';
import InfoTooltip from '../../../components/info-tooltip/info-tooltip';
import CollapsibleComponent from '../../../components/Collapsible/CollapsibleComponent';
import createNotification from '../../../utils/createNotification';
import { LegalTermCommentCell } from './LegalTermCommentCell';
import { VetValueInterpretationCell } from './VetValueInterpretationCell';
import { NotificationFlag } from '../../../components/NotificationFlag/NotificationFlag';
import { NotificationFlagsCategories } from '../../../models/NotificationFlags/NotificationFlagsCategories';
import '../../../components/LoiForm/LegalIssuesForm.scss';
import './LegalTermsSummaryTab.scss';

export interface LegalTermsSummaryTabProps {
    engagementId: number;
}

interface RowWithIndent {
    row: LegalTermRow;
    indentLevel: number;
}

const attachIndentLevels = (rows: LegalTermRow[]): RowWithIndent[] => {
    let currentHeaderLevel = 1;
    return rows.map((row) => {
        if (row.headerLevel != null && row.headerLevel >= 1) {
            currentHeaderLevel = row.headerLevel;
        }
        return { row, indentLevel: Math.min(currentHeaderLevel, 5) };
    });
};

const getRowClass = (headerLevel: RowWithIndent['row']['headerLevel'], indentLevel: number) => {
    if (headerLevel === 1) {
        return 'loi-form-row-header level-1';
    }
    if (headerLevel != null && headerLevel >= 2) {
        return `loi-form-row-subheader level-${Math.min(headerLevel, 5)}`;
    }
    return `loi-form-row-item item-under-${indentLevel}`;
};

const getRowTooltipText = (row: LegalTermRow) =>
    [row.guidance, row.tooltip]
        .filter((text): text is string => !!text)
        .join('<br/><br/>');

const hasCommentSlot = (row: LegalTermRow) => row.showValueInput;

const getLegalTermRowFlagPrefix = (engagementId: number, rowId: number) =>
    `${NotificationFlagsCategories.MpCode}.${engagementId}.${NotificationFlagsCategories.LegalTermsCode}.${rowId}.`;

const getLegalTermFieldFlagCode = (
    engagementId: number,
    rowId: number,
    field: 'INTERPRETATION' | 'SELLER_COMMENT' | 'BUYER_COMMENT'
) => `${getLegalTermRowFlagPrefix(engagementId, rowId)}${field}`;

export const LegalTermsSummaryTab = (props: LegalTermsSummaryTabProps) => {
    const axios = useStateSelector((state) => state.core.axios);
    const dispatch = useDispatch();
    const notificationFlags = useStateSelector((state) => state.notificationFlags.flags);
    const notificationFlagsRef = useRef(notificationFlags);
    const seenRowIdsRef = useRef<Set<number>>(new Set());
    const [summary, setSummary] = useState<LegalTermsSummary | null>(null);
    const [activeSubTab, setActiveSubTab] = useState<LegalTermsSummarySubTab>(
        LegalTermsSummarySubTab.LegalIssues
    );
    const [isLoading, setIsLoading] = useState(true);
    const [hasFailed, setHasFailed] = useState(false);
    const [openSectionId, setOpenSectionId] = useState<string | null>(null);

    useEffect(() => {
        notificationFlagsRef.current = notificationFlags;
    }, [notificationFlags]);

    useEffect(() => {
        if (!openSectionId || !summary) {
            return;
        }
        [...summary.legalIssueSections, ...summary.additionalCovenantSections]
            .filter((section) => section.id === openSectionId)
            .forEach((section) =>
                section.rows.forEach((row) => seenRowIdsRef.current.add(row.id))
            );
    }, [openSectionId, summary]);

    const clearSeenRowFlags = useCallback(
        (seenRowIds: Set<number>, engagementId: number) => {
            const seenPrefixes: string[] = [];
            seenRowIds.forEach((rowId) =>
                seenPrefixes.push(getLegalTermRowFlagPrefix(engagementId, rowId).toLowerCase())
            );
            notificationFlagsRef.current
                ?.filter((flag) =>
                    seenPrefixes.some((prefix) =>
                        flag.eventCode.toLowerCase().startsWith(prefix)
                    )
                )
                .forEach((flag) =>
                    dispatch(
                        addNotificationFlagToDelete({ ...flag, refreshAfterRemoval: true })
                    )
                );
            seenRowIds.clear();
        },
        [dispatch]
    );

    useEffect(() => {
        const seenRowIds = seenRowIdsRef.current;
        const engagementId = props.engagementId;
        return () => clearSeenRowFlags(seenRowIds, engagementId);
    }, [clearSeenRowFlags, props.engagementId]);

    const saveComment = useCallback(
        async (row: LegalTermRow, comment: string | null) => {
            try {
                setSummary(
                    await updateLegalTermRowComment(axios, props.engagementId, row.id, comment)
                );
            } catch (err) {
                createNotification(
                    err instanceof Error ? err.message : 'Failed to save the comment.',
                    'error'
                );
                throw err;
            }
        },
        [axios, props.engagementId]
    );

    const saveInterpretation = useCallback(
        async (row: LegalTermRow, value: string | null) => {
            try {
                setSummary(
                    await updateLegalTermRowInterpretation(
                        axios,
                        props.engagementId,
                        row.id,
                        value
                    )
                );
            } catch (err) {
                createNotification(
                    err instanceof Error ? err.message : 'Failed to save the interpretation.',
                    'error'
                );
                throw err;
            }
        },
        [axios, props.engagementId]
    );

    useEffect(() => {
        let isMounted = true;
        setIsLoading(true);
        setHasFailed(false);
        fetchLegalTermsSummary(axios, props.engagementId)
            .then((data) => {
                if (isMounted) {
                    setSummary(data);
                }
            })
            .catch(() => {
                if (isMounted) {
                    setHasFailed(true);
                }
            })
            .finally(() => {
                if (isMounted) {
                    setIsLoading(false);
                }
            });
        return () => {
            isMounted = false;
        };
    }, [axios, props.engagementId]);

    const renderRow = ({ row, indentLevel }: RowWithIndent) => {
        const tooltipText = getRowTooltipText(row);
        return (
            <div
                key={`${row.id}-${row.displayOrder}`}
                className={`loi-form-row ${getRowClass(row.headerLevel, indentLevel)}`}>
                <div className="loi-form-cell loi-form-cell-name">
                    <div className="loi-form-cell-name-with-tooltip">
                        <span>{row.name}</span>
                        {tooltipText ? (
                            <InfoTooltip
                                text={tooltipText}
                                idText={`legal-terms-summary-tooltip-${row.id}`}
                                className="loi-form-name-tooltip"
                            />
                        ) : null}
                    </div>
                </div>
                <div className="loi-form-cell loi-form-cell-guidance-at-loi">
                    {row.showValueInput
                        ? renderLoiAnswerReadonly(row.buyerGuidanceAtLoi, row.answerType)
                        : null}
                </div>
                <div className="loi-form-cell loi-form-cell-interpretation">
                    <span className="legal-terms-flagged legal-terms-flagged--interpretation">
                    {row.showValueInput ? (
                        <VetValueInterpretationCell
                            value={row.vetValueInterpretation}
                            answerType={row.answerType}
                            ariaLabel={row.name}
                            editable={row.canEditInterpretation}
                            onSave={(value) => saveInterpretation(row, value)}
                        />
                    ) : null}
                        <NotificationFlag
                            eventCode={getLegalTermFieldFlagCode(
                                props.engagementId,
                                row.id,
                                'INTERPRETATION'
                            )}
                            doNotRemove={true}
                        />
                    </span>
                </div>
                <div className="loi-form-cell loi-form-cell-seller-comment">
                    <span className="legal-terms-flagged legal-terms-flagged--comment">
                    {hasCommentSlot(row) ? (
                        <LegalTermCommentCell
                            value={row.sellerComment}
                            editable={row.canEditSellerComment}
                            uniqueId={`legal-terms-summary-seller-comment-${row.id}-${row.displayOrder}`}
                            onSave={(comment) => saveComment(row, comment)}
                        />
                    ) : null}
                        <NotificationFlag
                            eventCode={getLegalTermFieldFlagCode(
                                props.engagementId,
                                row.id,
                                'SELLER_COMMENT'
                            )}
                            doNotRemove={true}
                        />
                    </span>
                </div>
                <div className="loi-form-cell loi-form-cell-buyer-comment">
                    <span className="legal-terms-flagged legal-terms-flagged--comment">
                    {hasCommentSlot(row) ? (
                        <LegalTermCommentCell
                            value={row.buyerComment}
                            editable={row.canEditBuyerComment}
                            uniqueId={`legal-terms-summary-buyer-comment-${row.id}-${row.displayOrder}`}
                            onSave={(comment) => saveComment(row, comment)}
                        />
                    ) : null}
                        <NotificationFlag
                            eventCode={getLegalTermFieldFlagCode(
                                props.engagementId,
                                row.id,
                                'BUYER_COMMENT'
                            )}
                            doNotRemove={true}
                        />
                    </span>
                </div>
            </div>
        );
    };

    const renderSection = (section: LegalTermSection) => {
        const rows = [...section.rows].sort((a, b) => a.displayOrder - b.displayOrder);
        const isOpen = openSectionId === section.id;
        const sectionRowFlagPrefixes = Array.from(
            section.rows.map((row) =>
                getLegalTermRowFlagPrefix(props.engagementId, row.id).toLowerCase()
            )
        );
        return (
            <CollapsibleComponent
                key={section.id}
                isExpanded={isOpen}
                onToggle={(expanded) => setOpenSectionId(expanded ? section.id : null)}
                header={
                    <>
                        {section.name}
                        <NotificationFlag
                            eventCode={`legal-terms-section-${section.id}`}
                            doNotRemove={true}
                            matchPattern={(eventCode) =>
                                sectionRowFlagPrefixes.some((prefix) =>
                                    eventCode.toLowerCase().startsWith(prefix)
                                )
                            }
                        />
                    </>
                }>
                <div className="loi-form-section-body">
                    <div className="loi-form-grid">
                        {attachIndentLevels(rows).map(renderRow)}
                    </div>
                </div>
            </CollapsibleComponent>
        );
    };

    if (isLoading) {
        return (
            <div className="legal-terms-summary-tab loi-form legal-terms-summary-tab--loading">
                Loading...
            </div>
        );
    }

    if (hasFailed) {
        return (
            <div className="legal-terms-summary-tab loi-form legal-terms-summary-tab--empty">
                The legal terms summary could not be loaded. Please refresh the
                page.
            </div>
        );
    }

    const activeSections =
        activeSubTab === LegalTermsSummarySubTab.AdditionalNdaCovenants
            ? summary?.additionalCovenantSections ?? []
            : summary?.legalIssueSections ?? [];
    const sectionsOrdered = [...activeSections].sort((a, b) => a.order - b.order);

    const subTabRowFlagPrefixes = (subTab: LegalTermsSummarySubTab) =>
        Array.from(
            (subTab === LegalTermsSummarySubTab.AdditionalNdaCovenants
                ? summary?.additionalCovenantSections ?? []
                : summary?.legalIssueSections ?? []
            )
                .flatMap((section) => section.rows)
                .map((row) =>
                    getLegalTermRowFlagPrefix(props.engagementId, row.id).toLowerCase()
                )
        );

    const renderColumnHeader = () => (
        <div className="loi-form-grid loi-form-grid-header">
            <div className="loi-form-row loi-form-row-head">
                <div className="loi-form-cell loi-form-cell-name" />
                <div className="loi-form-cell loi-form-cell-guidance-at-loi">
                    Buyer&apos;s Guidance at LOI
                </div>
                <div className="loi-form-cell loi-form-cell-interpretation">
                    VetValue Interpretation
                </div>
                <div className="loi-form-cell loi-form-cell-seller-comment">
                    Seller&apos;s Comments
                </div>
                <div className="loi-form-cell loi-form-cell-buyer-comment">
                    Buyer&apos;s Comments
                </div>
            </div>
        </div>
    );

    return (
        <div className="legal-terms-summary-tab loi-form">
            <div className="RRT__container">
                <div className="RRT__tabs body-tabs">
                    {LegalTermsSummarySubTabs.map((item) => (
                        <div
                            key={item.id}
                            className={
                                activeSubTab === item.id
                                    ? 'RRT__tab RRT__tab--first RRT__tab--selected'
                                    : 'RRT__tab'
                            }
                            onClick={() => {
                                if (item.id !== activeSubTab) {
                                    clearSeenRowFlags(seenRowIdsRef.current, props.engagementId);
                                }
                                setActiveSubTab(item.id);
                                setOpenSectionId(null);
                            }}>
                            {item.content}
                            <NotificationFlag
                                eventCode={`legal-terms-sub-tab-${item.id}`}
                                doNotRemove={true}
                                matchPattern={(eventCode) =>
                                    subTabRowFlagPrefixes(item.id).some((prefix) =>
                                        eventCode.toLowerCase().startsWith(prefix)
                                    )
                                }
                            />
                        </div>
                    ))}
                </div>
            </div>
            {sectionsOrdered.length === 0 ? (
                <div className="legal-terms-summary-tab--empty">
                    No terms recorded.
                </div>
            ) : (
                <>
                    {renderColumnHeader()}
                    {sectionsOrdered.map(renderSection)}
                </>
            )}
        </div>
    );
};
